import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUpRight,
  CalendarRange,
  CheckCircle2,
  LayoutDashboard,
  Plus,
  Trophy,
} from 'lucide-react';
import { AdminModulePage, PageHero } from '../components/PageTemplates';
import { useStore } from '../hooks/useStore';
import { setupStatus } from '../services/tournamentService';
import { paths, tournamentSlug } from '../lib/paths';
import {
  buildAdminDashboardState,
  type AdminDashboardTask,
  type AdminDashboardActionCenter,
} from '../services/adminDashboardService';
import { buildGameOperationsSummary, buildLeagueSetupChecklist, type GameOperationGame, type LeagueSetupChecklist } from '../services/leagueOpsService';
import type { Tournament } from '../models';

const SETUP_DETAILS_KEY = 'scorekeeper.dashboard.setupDetails';

/**
 * League HQ dashboard. This page is an action center: it points users toward
 * the next operational step without duplicating every module's detail view.
 */
export function CompanyHomePage() {
  const db = useStore((d) => d);
  const dashboard = useMemo(() => buildAdminDashboardState(db), [db]);
  const setupChecklist = useMemo(() => buildLeagueSetupChecklist(db), [db]);
  const gameOps = useMemo(() => buildGameOperationsSummary(db), [db]);
  const setupComplete = setupChecklist.percent === 100;
  const [reviewSetup, setReviewSetup] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SETUP_DETAILS_KEY) === 'expanded';
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !setupComplete) return;
    window.localStorage.setItem(SETUP_DETAILS_KEY, reviewSetup ? 'expanded' : 'collapsed');
  }, [reviewSetup, setupComplete]);

  const hasGameSummary =
    gameOps.todayGames.length +
    gameOps.upcomingGames.length +
    gameOps.readyToScore.length +
    gameOps.needsScorekeeper.length +
    gameOps.needsOfficial.length +
    gameOps.recentFinals.length > 0;

  return (
    <>
      <MobileAdminHome
        dashboard={dashboard}
        checklist={setupChecklist}
        gameOps={gameOps}
        setupComplete={setupComplete}
        reviewSetup={reviewSetup}
        setReviewSetup={setReviewSetup}
        hasGameSummary={hasGameSummary}
      />

      <AdminModulePage>
      <div className="admin-desktop-home">
        <PageHero
          title="Action center"
          description={dashboard.actionCenter.message}
          icon={LayoutDashboard}
          accent="arc"
          actions={<ActionCenterActions actionCenter={dashboard.actionCenter} />}
        />

        <div style={{ marginTop: 16 }}>
          <GettingStartedCard
            checklist={setupChecklist}
            setupComplete={setupComplete}
            reviewSetup={reviewSetup}
            setReviewSetup={setReviewSetup}
          />
        </div>

        {dashboard.needsAction.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <NeedsActionCard tasks={dashboard.needsAction} nextBestAction={dashboard.nextBestAction} />
          </div>
        )}

        {(hasGameSummary || dashboard.currentEvents.length > 0) && (
          <div className="grid grid--2" style={{ marginTop: 16 }}>
            {hasGameSummary && <GamesSnapshotCard gameOps={gameOps} />}
            {dashboard.currentEvents.length > 0 && <CurrentEventsCard events={dashboard.currentEvents} />}
          </div>
        )}
      </div>
      </AdminModulePage>
    </>
  );
}

function GettingStartedCard({
  checklist,
  setupComplete,
  reviewSetup,
  setReviewSetup,
}: {
  checklist: LeagueSetupChecklist;
  setupComplete: boolean;
  reviewSetup: boolean;
  setReviewSetup: (value: boolean) => void;
}) {
  const next = checklist.items.find((item) => !item.complete);
  const showDetails = !setupComplete || reviewSetup;

  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title"><CheckCircle2 size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Getting started</h3>
          <p className="card__sub">
            {setupComplete
              ? 'Setup complete. Review the checklist any time.'
              : `${checklist.completed} of ${checklist.total} setup steps complete.`}
          </p>
        </div>
        <span className={`chip chip--${setupComplete ? 'ok' : 'info'}`}>{checklist.percent}%</span>
      </div>
      <div className="steps-compact__bar" aria-hidden style={{ marginBottom: 12 }}>
        <span style={{ width: `${checklist.percent}%` }} />
      </div>

      {!showDetails ? (
        <>
          <p className="muted" style={{ margin: 0 }}>
            Your league foundation is configured. The dashboard will stay focused on game-day and operational work.
          </p>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            <Link to={paths.adminTournaments} className="btn btn--primary btn--sm">
              All events <ArrowUpRight size={13} />
            </Link>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setReviewSetup(true)}>
              Review setup
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="stack" style={{ gap: 8 }}>
            {checklist.items.map((item, index) => (
              <Link key={item.id} to={item.href} className="setup-check-row">
                <span className={`setup-check-row__mark ${item.complete ? 'is-complete' : ''}`}>
                  {item.complete ? <CheckCircle2 size={14} /> : index + 1}
                </span>
                <span>
                  <strong>{item.label}</strong>
                  <span className="muted">{item.detail}</span>
                </span>
              </Link>
            ))}
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            {next ? (
              <Link to={next.href} className="btn btn--primary btn--sm">
                Next: {next.actionLabel} <ArrowUpRight size={13} />
              </Link>
            ) : (
              <Link to={paths.adminTournaments} className="btn btn--primary btn--sm">
                All events <ArrowUpRight size={13} />
              </Link>
            )}
            {setupComplete && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setReviewSetup(false)}>
                Hide setup details
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function NeedsActionCard({ tasks, nextBestAction }: { tasks: AdminDashboardTask[]; nextBestAction: AdminDashboardTask }) {
  return (
    <div className="card card--accent">
      <div className="card__head">
        <div>
          <h3 className="card__title"><AlertTriangle size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Needs action</h3>
          <p className="card__sub">
            {tasks.length > 0
              ? `${tasks.length} operational item${tasks.length === 1 ? '' : 's'} sorted by urgency.`
              : 'No urgent items right now.'}
          </p>
        </div>
        <Link to={nextBestAction.href} className="btn btn--ghost btn--sm">
          {nextBestAction.actionLabel ?? 'Open'} <ArrowUpRight size={12} />
        </Link>
      </div>
      {tasks.length === 0 ? (
        <div className="empty">Nothing needs attention. Use the quick links below when you are ready to keep building.</div>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          {tasks.slice(0, 6).map((task) => <TaskLink key={task.id} task={task} />)}
        </div>
      )}
    </div>
  );
}

function ActionCenterActions({ actionCenter }: { actionCenter: AdminDashboardActionCenter }) {
  return (
    <div className="action-center-actions">
      <Link to={actionCenter.primaryAction.href} className="btn btn--primary">
        {actionCenter.primaryAction.label} <ArrowUpRight size={14} />
      </Link>
      {actionCenter.showNewEventMenu ? (
        <details className="action-menu">
          <summary className="btn btn--ghost">
            <Plus size={14} /> New event
          </summary>
          <div className="action-menu__panel">
            {actionCenter.secondaryActions.map((action) => (
              <Link key={action.href} to={action.href} className="action-menu__item">
                {action.label}
              </Link>
            ))}
          </div>
        </details>
      ) : (
        actionCenter.secondaryActions.map((action) => (
          <Link key={action.href} to={action.href} className="btn btn--ghost">
            <Plus size={14} /> {action.label}
          </Link>
        ))
      )}
      <Link to={actionCenter.allEventsAction.href} className="btn btn--ghost">
        {actionCenter.allEventsAction.label} <ArrowUpRight size={14} />
      </Link>
    </div>
  );
}

function GamesSnapshotCard({ gameOps }: { gameOps: ReturnType<typeof buildGameOperationsSummary> }) {
  const openHref =
    gameOps.todayGames[0]?.href ??
    gameOps.readyToScore[0]?.href ??
    gameOps.upcomingGames[0]?.scheduleHref ??
    gameOps.needsScorekeeper[0]?.operationsHref ??
    gameOps.needsOfficial[0]?.operationsHref ??
    paths.adminTournaments;

  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title"><Trophy size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Today’s games</h3>
          <p className="card__sub">Scoring and assignment status across scheduled games.</p>
        </div>
        <Link to={openHref} className="btn btn--ghost btn--sm">Open <ArrowUpRight size={12} /></Link>
      </div>
      <div className="grid grid--4">
        <MiniMetric label="Today" value={gameOps.todayGames.length} tone={gameOps.todayGames.length > 0 ? 'info' : undefined} />
        <MiniMetric label="Ready" value={gameOps.readyToScore.length} tone={gameOps.readyToScore.length > 0 ? 'ok' : undefined} />
        <MiniMetric label="Scorekeepers" value={gameOps.needsScorekeeper.length} tone={gameOps.needsScorekeeper.length > 0 ? 'warn' : undefined} />
        <MiniMetric label="Officials" value={gameOps.needsOfficial.length} tone={gameOps.needsOfficial.length > 0 ? 'warn' : undefined} />
      </div>
    </div>
  );
}

function CurrentEventsCard({ events }: { events: Tournament[] }) {
  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title"><Trophy size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Current events</h3>
          <p className="card__sub">A compact path back into active seasons and tournaments.</p>
        </div>
        <Link to={paths.adminTournaments} className="btn btn--ghost btn--sm">All events <ArrowUpRight size={12} /></Link>
      </div>
      {events.length === 0 ? (
        <div className="empty">No events yet. Create a season or tournament to begin.</div>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          {events.map((event) => <EventLink key={event.id} event={event} />)}
        </div>
      )}
    </div>
  );
}

function TaskLink({ task }: { task: AdminDashboardTask }) {
  return (
    <Link to={task.href} className="setup-check-row">
      <span className={`setup-check-row__mark ${task.tone === 'ok' ? 'is-complete' : ''}`}>
        {task.tone === 'warn' ? <AlertTriangle size={14} /> : task.tone === 'ok' ? <CheckCircle2 size={14} /> : <ArrowUpRight size={14} />}
      </span>
      <span>
        <strong>{task.label}</strong>
        <span className="muted">{task.detail}</span>
      </span>
    </Link>
  );
}

function EventLink({ event }: { event: Tournament }) {
  const status = setupStatus(event);
  const fmt = (event.format ?? 'tournament') === 'season' ? 'Season' : 'Tournament';
  return (
    <Link to={paths.adminCompetition(event.format, tournamentSlug(event))} className="setup-check-row">
      <span className="setup-check-row__mark">
        {(event.format ?? 'tournament') === 'season' ? <CalendarRange size={14} /> : <Trophy size={14} />}
      </span>
      <span>
        <strong>{event.name}</strong>
        <span className="muted">
          {fmt} - {event.status.replace('_', ' ')} - {status.label} - {new Date(event.startsAt).toLocaleDateString()}
        </span>
      </span>
    </Link>
  );
}

function MiniMetric({ label, value, tone }: { label: string; value: number; tone?: 'ok' | 'warn' | 'info' }) {
  const color = tone === 'ok' ? 'var(--ok)' : tone === 'warn' ? 'var(--warn)' : tone === 'info' ? 'var(--info)' : 'var(--text)';
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="eyebrow">{label}</div>
      <div style={{ fontSize: 22, lineHeight: 1, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

function MobileAdminHome({
  dashboard,
  checklist,
  gameOps,
  setupComplete,
  reviewSetup,
  setReviewSetup,
  hasGameSummary,
}: {
  dashboard: ReturnType<typeof buildAdminDashboardState>;
  checklist: LeagueSetupChecklist;
  gameOps: ReturnType<typeof buildGameOperationsSummary>;
  setupComplete: boolean;
  reviewSetup: boolean;
  setReviewSetup: (value: boolean) => void;
  hasGameSummary: boolean;
}) {
  return (
    <div className="admin-mobile-home">
      <PageHero
        title="Action center"
        description={dashboard.actionCenter.message}
        icon={LayoutDashboard}
        accent="arc"
        actions={<ActionCenterActions actionCenter={dashboard.actionCenter} />}
      />

      <MobileSection title="Getting started">
        <GettingStartedCard
          checklist={checklist}
          setupComplete={setupComplete}
          reviewSetup={reviewSetup}
          setReviewSetup={setReviewSetup}
        />
      </MobileSection>

      {dashboard.needsAction.length > 0 && (
        <MobileSection title="Needs action">
          <div className="mobile-card-stack">
            {dashboard.needsAction.slice(0, 6).map((task) => <TaskLink key={task.id} task={task} />)}
          </div>
        </MobileSection>
      )}

      {hasGameSummary && (
        <MobileSection title="Today’s games" action={{ to: gameOps.todayGames[0]?.href ?? gameOps.readyToScore[0]?.href ?? paths.adminTournaments, label: 'Open' }}>
          <div className="mobile-timeline">
            {gameOps.todayGames.length > 0
              ? gameOps.todayGames.slice(0, 8).map((item) => <TodayGameRow key={item.game.id} item={item} />)
              : (
                  <div className="grid grid--4">
                    <MiniMetric label="Ready" value={gameOps.readyToScore.length} tone={gameOps.readyToScore.length > 0 ? 'ok' : undefined} />
                    <MiniMetric label="Crew" value={gameOps.needsScorekeeper.length} tone={gameOps.needsScorekeeper.length > 0 ? 'warn' : undefined} />
                    <MiniMetric label="Refs" value={gameOps.needsOfficial.length} tone={gameOps.needsOfficial.length > 0 ? 'warn' : undefined} />
                    <MiniMetric label="Upcoming" value={gameOps.upcomingGames.length} tone={gameOps.upcomingGames.length > 0 ? 'info' : undefined} />
                  </div>
                )}
          </div>
        </MobileSection>
      )}

      {dashboard.currentEvents.length > 0 && (
        <MobileSection title="Current events" action={{ to: paths.adminTournaments, label: 'All' }}>
          <div className="mobile-card-stack">
            {dashboard.currentEvents.slice(0, 4).map((event) => <EventLink key={event.id} event={event} />)}
          </div>
        </MobileSection>
      )}
    </div>
  );
}

function TodayGameRow({ item }: { item: GameOperationGame }) {
  const startsAt = item.slot?.startsAt ?? item.game.updatedAt;
  return (
    <Link to={item.href} className="mobile-timeline-row">
      <div className="mobile-timeline-row__time">{new Date(startsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
      <div>
        <div className="mobile-timeline-row__match">{item.home?.name ?? 'Home'} vs {item.away?.name ?? 'Away'}</div>
        <div className="muted" style={{ fontSize: 12 }}>{item.tournament?.name ?? 'Event'}</div>
      </div>
    </Link>
  );
}

function MobileSection({ title, action, children }: { title: string; action?: { to: string; label: string }; children: ReactNode }) {
  return (
    <section className="mobile-dashboard-section">
      <div className="mobile-section-head">
        <h3>{title}</h3>
        {action && <Link to={action.to}>{action.label}</Link>}
      </div>
      {children}
    </section>
  );
}
